import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, Navigation, Star } from "lucide-react";
import ConciergeMap, { MapHotel, MapPoint } from "./map/ConciergeMap";
import { categoryMeta, glyphSvg, humanize } from "./map/categories";
import localization from "../../../../../assets/localization";

interface PoiMapTemplateProps {
  template: any;
  language: string;
}

type Poi = MapPoint & {
  walkMin: number | null;
  description: string;
  reviews: number | null;
  photoUrl: string | null;
  subtype: string | null;
  openingHours: string | null;
  cuisine: string[];
};

const parseRating = (raw: any): number | null => {
  const n = Number(raw);
  return isNaN(n) || n <= 0 ? null : Math.round(n * 10) / 10;
};

const parseCuisine = (raw: any): string[] =>
  typeof raw === "string"
    ? raw
        .split(";")
        .map((c) => humanize(c))
        .filter(Boolean)
        .slice(0, 3)
    : [];

// Rich enrichment (rating/photo/hours/cuisine) is TripAdvisor top-N only, so
// every field here is optional and the card must read well without any of it.
function PoiExtras({ poi, t }: { poi: Poi; t: any }) {
  const hasRating = poi.rating != null;
  const hasHours = !!poi.openingHours;
  const hasCuisine = poi.cuisine.length > 0;
  if (!hasRating && !hasHours && !hasCuisine) return null;
  return (
    <>
      {hasRating && (
        <div
          className="cmap-card-rating"
          title={
            poi.reviews != null
              ? `${poi.rating} · ${poi.reviews.toLocaleString()} ${t.reviews}`
              : String(poi.rating)
          }
        >
          <Star className="cmap-star" />
          <span className="cmap-rating-num">{poi.rating}</span>
          {poi.reviews != null && (
            <span className="cmap-rating-count">
              ({poi.reviews.toLocaleString()})
            </span>
          )}
        </div>
      )}
      {hasHours && (
        <div className="cmap-card-hours" title={poi.openingHours!}>
          <Clock className="icon" />
          <span>{poi.openingHours}</span>
        </div>
      )}
      {hasCuisine && (
        <div className="cmap-card-tags">
          {poi.cuisine.map((c, i) => (
            <span key={i} className="cmap-tag">
              {c}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

export const parseHotel = (raw: any): MapHotel => {
  const lat = Number(raw?.lat);
  const lng = Number(raw?.long ?? raw?.lng);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { name: raw?.name ?? "", lat, lng };
};

export const parsePois = (rawPoints: any[]): Poi[] =>
  (rawPoints ?? [])
    .map((point: any) => {
      const lat = Number(point?.lat);
      const lng = Number(point?.long ?? point?.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return {
        name: point.name ?? "",
        lat,
        lng,
        category: point.category ?? null,
        rating: parseRating(point.rating),
        walkMin: point.walk_min ?? point.walkMin ?? null,
        description: point.description ?? "",
        reviews:
          point.reviews != null && !isNaN(Number(point.reviews))
            ? Number(point.reviews)
            : null,
        photoUrl: point.photo_url ?? point.photoUrl ?? null,
        subtype: point.subtype ?? null,
        openingHours: point.opening_hours ?? point.openingHours ?? null,
        cuisine: parseCuisine(point.cuisine),
      };
    })
    .filter(Boolean) as Poi[];

const directionsUrl = (point: MapPoint) =>
  `https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lng}&travelmode=walking`;

// "Nei dintorni" map: the hotel plus the places the concierge just talked
// about, on one interactive map with a floating card rail.
export default function PoiMapTemplate({
  template,
  language,
}: PoiMapTemplateProps) {
  const t = localization.get(language);
  const hotel = useMemo(() => parseHotel(template?.hotel), [template]);
  const pois = useMemo(() => parsePois(template?.points), [template]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const railRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    railRef.current
      ?.querySelector(".cmap-card.active")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedIndex]);

  if (!pois.length) return null;

  return (
    <div className="cmap-template">
      <ConciergeMap
        hotel={hotel}
        points={pois}
        mode="map"
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        language={language}
      />

      <div className="cmap-head">
        <span className="cmap-head-title">
          {template?.title || t.anchor_places}
        </span>
        <span className="cmap-head-sub">
          {pois.length} {pois.length === 1 ? t.cat_place.toLowerCase() : t.places}
        </span>
      </div>

      <div className="cmap-rail" ref={railRef}>
        {pois.map((poi, index) => {
          const meta = categoryMeta(poi.category);
          const subtype = humanize(poi.subtype);
          return (
            <div
              key={index}
              className={`cmap-card ${poi.photoUrl ? "has-photo" : ""} ${
                index === selectedIndex ? "active" : ""
              }`}
              onClick={() => setSelectedIndex(index)}
            >
              {poi.photoUrl && (
                <div className="cmap-card-photo">
                  <img
                    src={poi.photoUrl}
                    alt={poi.name}
                    loading="lazy"
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.parentElement?.style.setProperty("display", "none");
                    }}
                  />
                </div>
              )}
              <div className="cmap-card-body">
                <div className="cmap-card-head">
                  <span
                    className="cmap-card-chip"
                    style={{ background: meta.color }}
                    dangerouslySetInnerHTML={{
                      __html: glyphSvg(meta.glyph, 13),
                    }}
                  />
                  <span className="cmap-card-name">{poi.name}</span>
                </div>
                <div className="cmap-card-meta">
                  {(t as any)[meta.labelKey]}
                  {poi.walkMin != null && (
                    <>
                      <span className="cmap-dot">·</span>
                      {poi.walkMin} {t.walk_min}
                    </>
                  )}
                </div>
                {subtype && (
                  <div className="cmap-card-subrow">
                    <span className="cmap-subtype">{subtype}</span>
                  </div>
                )}
                <PoiExtras poi={poi} t={t} />
                {poi.description && (
                  <div className="cmap-card-desc">{poi.description}</div>
                )}
                <div className="cmap-card-actions">
                  <a
                    href={directionsUrl(poi)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Navigation className="icon" />
                    {t.directions}
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
